import React, { forwardRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Text } from '../../Kit';
import { useLanguage } from '../../../Store/modules/language';
import { HtmlWithTables } from '../HTMLWithTables/HTMLWithTables';

export type SmoothTextProps = {
  text: string;
};

export const SmoothText = forwardRef<HTMLDivElement, SmoothTextProps>(({ text }, ref) => {
  const { direction } = useLanguage();

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.5,
      },
    },
  };

  const paragraphVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const groupedParagraphs = useMemo(() => {
    const paragraphs = (text ?? '').split('\n');
    return paragraphs.reduce<string[]>((acc, paragraph, index) => {
      const groupIndex = Math.floor(index / 10);
      if (!acc[groupIndex]) {
        acc[groupIndex] = paragraph;
      } else {
        acc[groupIndex] += '\n' + paragraph;
      }
      return acc;
    }, []);
  }, [text]);

  return (
    <Text
      elementType="div"
      lineHeight={2.2}
      translatable={false}
      className="text-sm"
      fontWeight={400}
    >
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="response-content-text print:p-[16mm] print:box-border"
        dir={direction}
      >
        {groupedParagraphs.map((chunk, index) => (
          <motion.div
            key={index}
            className="text-justify"
            dir={direction}
            variants={paragraphVariants}
          >
            <HtmlWithTables htmlString={chunk} />
          </motion.div>
        ))}
      </motion.div>
    </Text>
  );
});

SmoothText.displayName = 'SmoothText';
